import { Button, Result, Spin } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { verifyEmail } from '../api'
import type { SaasApiError } from '../api'

type Status = 'checking' | 'success' | 'error'

/**
 * Mounted at `/verify/:token` — the link inside the verification email
 * (`{PUBLIC_ADMIN_BASE_URL}/verify/{token}`, see app/core/email.py).
 * Calls `/auth/verify` once on mount and redirects to `/login` shortly
 * after success; on failure (invalid/expired/already-used token) leaves
 * the user on this page with a way to request a new link.
 */
export default function VerifyEmailPage() {
  const { token } = useParams<{ token: string }>()
  const [status, setStatus] = useState<Status>('checking')
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const calledRef = useRef(false)

  useEffect(() => {
    if (!token || calledRef.current) return
    calledRef.current = true
    verifyEmail(token)
      .then(() => {
        setStatus('success')
        const t = window.setTimeout(() => navigate('/login?verified=1', { replace: true }), 2000)
        return () => window.clearTimeout(t)
      })
      .catch((err) => {
        setStatus('error')
        setError((err as SaasApiError).message || 'Verification failed')
      })
  }, [token, navigate])

  return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5' }}>
      {status === 'checking' && <Spin size="large" tip="Verifying your email…" />}
      {status === 'success' && (
        <Result status="success" title="Email verified" subTitle="Redirecting you to login…" />
      )}
      {status === 'error' && (
        <Result
          status="error"
          title="Verification failed"
          subTitle={error || 'This link is invalid or has expired.'}
          extra={
            <Link to="/login">
              <Button type="primary">Back to login</Button>
            </Link>
          }
        />
      )}
    </div>
  )
}
